import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatEUR, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Card, CardHead, Stat } from '../components/ui';
import { IconBilling } from '../components/Icons';

export function Billing() {
  const { t } = useTranslation();
  const lang = useLang();
  const est = useAsync(() => api.billingEstimate(), []);
  // The rate card is loaded separately and rendered unconditionally: a customer
  // with no usage yet still needs to see what things cost.
  const pricing = useAsync(() => api.pricing(), []);

  return (
    <>
      <PageHeader title={t('billing.title')} subtitle={t('billing.subtitle')} />

      {pricing.data ? (
        <div className="stack" style={{ marginBottom: 16 }}>
        <Card>
          <CardHead title={t('billing.rateCard')}>
            {pricing.data.planName ? (
              <span className="muted" style={{ fontSize: 13 }}>
                {pricing.data.planName} (v{pricing.data.planVersion})
              </span>
            ) : null}
          </CardHead>
          {pricing.data.hasPlan ? (
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  <tr>
                    <td>{t('billing.rateWrite')}</td>
                    <td className="num cell-strong">
                      {t('billing.perGb', { price: formatEUR(pricing.data.writeEurPerGb, lang) })}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('billing.rateStorage')}</td>
                    <td className="num cell-strong">
                      {t('billing.perGbYear', { price: formatEUR(pricing.data.storageEurPerGbYear, lang) })}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('billing.rateRead')}</td>
                    <td className="num cell-strong">
                      {t('billing.perReadout', { price: formatEUR(pricing.data.readEurPerReadout, lang) })}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('billing.rateFreeReadouts')}</td>
                    <td className="num cell-strong">
                      {formatNumber(pricing.data.freeReadoutsLifetime, lang)}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {t('billing.rateMinBillable')}
                      <br />
                      <span className="muted" style={{ fontSize: 12 }}>{t('billing.rateMinBillableHint')}</span>
                    </td>
                    <td className="num cell-strong">
                      {formatBytes(pricing.data.minBillableObjectBytes, lang)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card__body">
              <p className="muted" style={{ margin: 0 }}>{t('billing.noPlan')}</p>
            </div>
          )}
        </Card>
        </div>
      ) : null}

      <ApiState {...est} onRetry={est.reload}>
        {(data) =>
          data.records.length === 0 ? (
            <Card>
              <EmptyState title={t('billing.empty')} icon={<IconBilling width={26} height={26} />} />
            </Card>
          ) : (
            <div className="stack">
              <div className="grid grid--stats">
                <Stat label={t('billing.total')} value={formatEUR(data.totalEUR, lang)} icon={<IconBilling width={16} height={16} />} />
                {data.currentStorageEurPerMonth ? (
                  <Stat
                    label={t('billing.storagePerMonth')}
                    value={formatEUR(data.currentStorageEurPerMonth, lang)}
                    icon={<IconBilling width={16} height={16} />}
                  />
                ) : null}
                {data.planName ? (
                  <Stat label={t('billing.pricedBy')} value={`${data.planName} (v${data.planVersion})`} />
                ) : null}
              </div>
              <Card>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('billing.colPeriod')}</th>
                        <th className="num">{t('billing.colWritten')}</th>
                        <th className="num">{t('billing.colRetrieved')}</th>
                        <th className="num">{t('billing.colEur')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.records.map((r) => (
                        <tr key={r.period}>
                          <td className="cell-strong">{r.period}</td>
                          <td className="num">{formatNumber(r.mediaWritten, lang)}</td>
                          <td className="num">{formatNumber(r.mediaRetrieved, lang)}</td>
                          <td className="num">{formatEUR(r.eur, lang)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="cell-strong">{t('billing.total')}</td>
                        <td />
                        <td />
                        <td className="num cell-strong">{formatEUR(data.totalEUR, lang)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </div>
          )
        }
      </ApiState>
    </>
  );
}
